import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import "../css/MissionsTab.css"; // reuse the same styles

// helper: simple slug generator (same rules you use elsewhere)
function slugify(s = "") {
  return s
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

// optional thumbnail fallback (matches MissionsTab map fallback idea)
const DEFAULT_TIP_COVER = "/assets/gta-tip-cover.jpg";

export default function TipsTab({ user }) {
  const isAuthed = Boolean(user?.uid);
  const isAdmin = ["admin", "superadmin", "contributor"]
    .includes(String(user?.role || "").toLowerCase());

  const [form, setForm] = useState({
    // Basics
    title: "",
    summary: "",
    tagsCsv: "",
    coverImage: "",
    gallery: [""],
    videoUrl: "",
    // Content (Markdown)
    content: "",
  });

  const [saving, setSaving] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null); // { id, slug }
  const [error, setError] = useState("");
  const [previews, setPreviews] = useState({
    summary: false,
    content: false,
  });

  const updateField = (name, value) => setForm((s) => ({ ...s, [name]: value }));
  const updateArrayField = (arrKey, idx, value) =>
    setForm((s) => {
      const arr = [...s[arrKey]];
      arr[idx] = value;
      return { ...s, [arrKey]: arr };
    });
  const addArrayRow = (arrKey, blank = "") =>
    setForm((s) => ({ ...s, [arrKey]: [...s[arrKey], blank] }));
  const removeArrayRow = (arrKey, idx) =>
    setForm((s) => {
      const arr = s[arrKey].filter((_, i) => i !== idx);
      return { ...s, [arrKey]: arr.length ? arr : [""] };
    });
  const togglePreview = (key) =>
    setPreviews((p) => ({ ...p, [key]: !p[key] }));

  // ---------- validation ----------
  const issues = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (!isAdmin) errs.push("You do not have permission to add tips.");

    if (!form.title.trim()) errs.push("Title is required.");
    if (!form.summary.trim()) errs.push("Summary is required.");
    if (!form.content.trim()) errs.push("Content (Markdown) is required.");

    // URL checks
    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try { new URL(v.trim()); return true; } catch { return false; }
    };
    if (form.coverImage && !urlLike(form.coverImage)) errs.push("Cover Image URL is invalid.");
    if (form.videoUrl && !urlLike(form.videoUrl)) errs.push("Video URL is invalid.");

    form.gallery.forEach((u, i) => {
      if (u.trim() && !urlLike(u)) errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    // Soft length hints
    if (form.summary.trim().length > 300) errs.push("Summary is too long (keep under 300 characters).");

    return errs;
  }, [form, isAdmin, isAuthed]);

  const canSubmit = issues.length === 0 && !saving;

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError("");
    setSuccessInfo(null);

    try {
      const slug = slugify(form.title);
      const payload = {
        title: form.title.trim(),
        slug, // note: ensure uniqueness at write-time in your editor if needed
        summary: form.summary.trim(),
        content: form.content.trim(),
        tags: form.tagsCsv
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        coverImage: form.coverImage.trim() || null,
        gallery: form.gallery.map((g) => g.trim()).filter(Boolean),
        videoUrl: form.videoUrl.trim() || null,
        // meta
        authorId: user.uid,
        authorName: user.displayName || user.name || "Anonymous",
        reactions: { like: 0, fire: 0, wow: 0 },
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const ref = await addDoc(collection(db, "tips_tricks"), payload);
      setSuccessInfo({ id: ref.id, slug });

      // reset
      setForm({
        title: "",
        summary: "",
        tagsCsv: "",
        coverImage: "",
        gallery: [""],
        videoUrl: "",
        content: "",
      });
      setPreviews({ summary: false, content: false });
    } catch (err) {
      console.error(err);
      setError("Could not save tip. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // computed cover for preview tile
  const coverForPreview = form.coverImage?.trim() || DEFAULT_TIP_COVER;

  return (
    <div className="mt6-wrap">
      <div className="mt6-card">
        <div className="mt6-header">
          <div>
            <h3 className="mt6-title">Add GTA VI Tip</h3>
            <p className="mt6-sub">
              Admin tool to insert community Tips & Tricks: summary, full guide (Markdown), tags, media.
            </p>
          </div>
        </div>

        {!isAuthed && <div className="mt6-banner mt6-banner--warn">Please sign in.</div>}
        {isAuthed && !isAdmin && (
          <div className="mt6-banner mt6-banner--error">You don’t have permission to add tips.</div>
        )}
        {error && <div className="mt6-banner mt6-banner--error">{error}</div>}
        {successInfo && (
          <div className="mt6-banner mt6-banner--ok">
            Tip saved (ID: <code>{successInfo.id}</code>)
            {" · "}
            <a
              href={`/community/tips_tricks/${successInfo.slug || successInfo.id}`}
              target="_blank"
              rel="noreferrer"
            >
              View on site →
            </a>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt6-form">
          {/* Basics */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Basics</h4>

            <label className="mt6-label">Title</label>
            <input
              className="mt6-input"
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="e.g., Escape 5★ wanted level consistently"
              required
              disabled={!isAdmin || saving}
            />

            <div className="mt6-row-2">
              <div>
                <label className="mt6-label">Summary (≤300 chars)</label>
                <div className="mt6-segment">
                  <button
                    type="button"
                    className={`mt6-seg-btn ${!previews.summary ? "is-active": ""}`}
                    onClick={() => togglePreview("summary")}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    className={`mt6-seg-btn ${previews.summary ? "is-active": ""}`}
                    onClick={() => togglePreview("summary")}
                  >
                    Preview
                  </button>
                </div>
                {!previews.summary ? (
                  <textarea
                    className="mt6-input mt6-input--textarea"
                    value={form.summary}
                    onChange={(e) => updateField("summary", e.target.value)}
                    placeholder="One-paragraph overview…"
                    rows={5}
                    disabled={!isAdmin || saving}
                  />
                ) : (
                  <div className="mt6-preview">
                    {form.summary.trim() ? (
                      <ReactMarkdown>{form.summary}</ReactMarkdown>
                    ) : (
                      <span className="mt6-muted">Nothing to preview.</span>
                    )}
                  </div>
                )}
                <div className="mt6-right mt6-muted">
                  {form.summary.trim().length}/300
                </div>
              </div>

              <div>
                <label className="mt6-label">Cover Image URL</label>
                <input
                  className="mt6-input"
                  value={form.coverImage}
                  onChange={(e) => updateField("coverImage", e.target.value)}
                  placeholder="https://…"
                  disabled={!isAdmin || saving}
                />
                <div className="mt6-thumbpreview">
                  <img src={coverForPreview} alt="Cover preview" />
                </div>
              </div>
            </div>

            <div className="mt6-row-2">
              <div>
                <label className="mt6-label">Tags (CSV)</label>
                <input
                  className="mt6-input"
                  value={form.tagsCsv}
                  onChange={(e) => updateField("tagsCsv", e.target.value)}
                  placeholder="money, driving, cops, combat"
                  disabled={!isAdmin || saving}
                />
              </div>
              <div>
                <label className="mt6-label">Video URL (optional)</label>
                <input
                  className="mt6-input"
                  value={form.videoUrl}
                  onChange={(e) => updateField("videoUrl", e.target.value)}
                  placeholder="https://youtube.com/…"
                  disabled={!isAdmin || saving}
                />
              </div>
            </div>

            <label className="mt6-label">Gallery URLs (optional)</label>
            {form.gallery.map((u, i) => (
              <div className="mt6-row" key={`g-${i}`}>
                <input
                  className="mt6-input"
                  value={u}
                  onChange={(e) => updateArrayField("gallery", i, e.target.value)}
                  placeholder={`Image URL #${i + 1}`}
                  disabled={!isAdmin || saving}
                />
                <button
                  type="button"
                  className="mt6-remove"
                  onClick={() => removeArrayRow("gallery", i)}
                  disabled={!isAdmin || saving || form.gallery.length <= 1}
                >✕</button>
              </div>
            ))}
            <button
              type="button"
              className="mt6-add"
              onClick={() => addArrayRow("gallery")}
              disabled={!isAdmin || saving}
            >＋ Add Image</button>
          </div>

          {/* Main content */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Full Guide (Markdown)</h4>
            <div className="mt6-segment">
              <button
                type="button"
                className={`mt6-seg-btn ${!previews.content ? "is-active": ""}`}
                onClick={() => togglePreview("content")}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previews.content ? "is-active": ""}`}
                onClick={() => togglePreview("content")}
              >
                Preview
              </button>
            </div>
            {!previews.content ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                value={form.content}
                onChange={(e) => updateField("content", e.target.value)}
                placeholder={`Use Markdown: 
- **Step 1:** Do X
- **Step 2:** Go to Y
- Pro tips, loadouts, routes, and gotchas…`}
                rows={14}
                disabled={!isAdmin || saving}
              />
            ) : (
              <div className="mt6-preview">
                {form.content.trim() ? (
                  <ReactMarkdown>{form.content}</ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>

          {/* Validation issues */}
          {issues.length > 0 && (
            <ul className="mt6-issues" role="alert">
              {issues.map((m, i) => <li key={i}>• {m}</li>)}
            </ul>
          )}

          {/* Actions */}
          <div className="mt6-actions">
            <button type="submit" className="mt6-btn mt6-btn--primary" disabled={!canSubmit}>
              {saving ? "Saving…" : "Save Tip"}
            </button>
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              disabled={saving}
              onClick={() => {
                setForm({
                  title: "",
                  summary: "",
                  tagsCsv: "",
                  coverImage: "",
                  gallery: [""],
                  videoUrl: "",
                  content: "",
                });
                setPreviews({ summary: false, content: false });
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
